import type React from "react";
import ReactDOM from "react-dom/client";

export interface PdfExportOptions {
  filename?: string;
  orientation?: "portrait" | "landscape";
  format?: "a4" | "a3" | "letter";
  quality?: number;
  scale?: number;
  margin?: number | number[];
}

/**
 * HTMLElementを直接PDFとしてエクスポートする
 * @param element エクスポートするHTML要素またはセレクター
 * @param options PDFエクスポートオプション
 */
export const exportElementToPdf = async (
  element: HTMLElement | string,
  options: PdfExportOptions = {},
) => {
  const {
    filename = "document.pdf",
    orientation = "portrait",
    format = "a4",
    quality = 0.95,
    scale = 2,
    margin = 10,
  } = options;

  // html2pdf.jsを動的インポート
  const html2pdf = (await import("html2pdf.js")).default;

  // エレメントを取得
  const targetElement =
    typeof element === "string" ? (document.querySelector(element) as HTMLElement) : element;

  if (!targetElement) {
    throw new Error("Export element not found");
  }

  // PDFエクスポート設定
  const exportOptions = {
    margin: Array.isArray(margin)
      ? (margin as [number, number, number, number])
      : ([margin, margin, margin, margin] as [number, number, number, number]),
    filename,
    image: {
      type: "jpeg" as const,
      quality,
    },
    html2canvas: {
      scale,
      useCORS: true,
      letterRendering: true,
      logging: false,
      windowWidth: targetElement.scrollWidth,
      windowHeight: targetElement.scrollHeight,
      width: targetElement.scrollWidth,
      height: targetElement.scrollHeight,
      onclone: (clonedDoc: Document) => {
        // クローンされたドキュメントのスタイルを調整
        const clonedElement = clonedDoc.querySelector("body");
        if (clonedElement) {
          clonedElement.style.margin = "0";
          clonedElement.style.padding = "0";
        }
      },
    },
    jsPDF: {
      unit: "mm" as const,
      format,
      orientation,
      compress: true,
    },
    pagebreak: {
      mode: ["css", "legacy"],
      before: ".pdf-page-break-before",
      after: ".pdf-page-break-after",
      avoid: ["tr", "td", "th", "img", "h1", "h2", "h3"],
    },
    enableLinks: false,
  };

  // PDFを生成して保存
  await html2pdf().set(exportOptions).from(targetElement).save();
};

/**
 * React要素をPDFとしてエクスポートする
 * @param element エクスポートするReact要素
 * @param options PDFエクスポートオプション
 */
export const exportReactToPdf = async (
  element: React.ReactElement,
  options: PdfExportOptions = {},
) => {
  const {
    filename = "document.pdf",
    orientation = "portrait",
    format = "a4",
    quality = 0.95,
    scale = 2,
    margin = 10,
  } = options;

  // html2pdf.jsを動的インポート
  const html2pdf = (await import("html2pdf.js")).default;

  // 一時的なコンテナを作成
  const container = document.createElement("div");
  container.style.position = "absolute";
  container.style.left = "-9999px";
  container.style.top = "-9999px";
  container.style.width = orientation === "portrait" ? "794px" : "1123px"; // A4サイズ
  container.style.background = "white";
  container.style.padding = "24px";
  container.style.color = "#111";
  container.style.lineHeight = "1.6";
  container.style.fontFamily =
    '"Noto Sans JP", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif';
  document.body.appendChild(container);

  // Reactコンポーネントをレンダリング
  const root = ReactDOM.createRoot(container);

  // レンダリングして少し待つ
  await new Promise<void>((resolve) => {
    root.render(element);
    setTimeout(resolve, 500); // レンダリング完了を待つ
  });

  // PDFエクスポート設定
  const exportOptions = {
    margin: typeof margin === "number" ? margin : (margin as [number, number, number, number]),
    filename,
    image: { type: "jpeg" as const, quality },
    html2canvas: {
      scale,
      useCORS: true,
      letterRendering: true,
      logging: false,
    },
    jsPDF: {
      unit: "mm" as const,
      format,
      orientation,
    },
    pagebreak: {
      mode: ["css", "legacy"],
      before: ".pdf-page-break-before",
      after: ".pdf-page-break-after",
    },
  };

  // PDFを生成して保存
  await html2pdf().set(exportOptions).from(container).save();

  // クリーンアップ
  root.unmount();
  document.body.removeChild(container);
};
